import React from "react";
import { Box, TableCell, Tooltip, Typography } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import * as customColors from "../../../../../theme/customColors";

export const haklaiColumnBorder = "1px solid " + customColors.lightGray;
export const haklaiNameBorder = "2px solid " + (customColors.greyAndBlue || "#b0bec5");
export const haklaiNameColumnWidth = 208;
export const haklaiSectionFrameBorder = "3px solid rgba(51, 107, 136)";

// Calc tables share the תוצאות חישוב chrome from `.haklai-data-table`.
// Cells use logical borders so an override that clears them stays on the
// same edge in RTL and LTR. Vertical rules stay off; rows separate with
// the bottom border from that shared class.
const haklaiDataHeaderCell = {
  backgroundColor: "#d9e7f2",
  color: "#1e3d66",
  fontWeight: 600,
  textAlign: "center",
  padding: "8px 6px",
  whiteSpace: "normal",
  lineHeight: 1.25,
  fontSize: "13px",
  borderBottom: "3px solid #ff9800",
  borderInlineStart: "none",
  borderInlineEnd: "none",
  boxShadow: "inset -1px 0 0 rgba(30, 61, 102, 0.1)"
};

export const HaklaiStyledHeaderCell = withStyles({
  root: {
    ...haklaiDataHeaderCell,
    "&.MuiTableCell-sizeSmall:last-child": {
      paddingRight: 6
    }
  }
})(TableCell);

export const HaklaiStyledNameHeaderCell = withStyles({
  root: {
    ...haklaiDataHeaderCell,
    width: haklaiNameColumnWidth
  }
})(TableCell);

export const HaklaiStyledCell = withStyles({
  root: {
    padding: "8px 6px",
    verticalAlign: "middle",
    color: "#1e3d66",
    borderBottom: "1px solid #e8eef3",
    borderInlineStart: "none",
    borderInlineEnd: "none",
    "&.MuiTableCell-sizeSmall:last-child": {
      paddingRight: 6
    }
  }
})(TableCell);

export const HaklaiStyledNameCell = withStyles({
  root: {
    padding: "8px 6px",
    verticalAlign: "middle",
    color: "#1e3d66",
    borderBottom: "1px solid #e8eef3",
    borderInlineStart: "none",
    borderInlineEnd: "none",
    width: haklaiNameColumnWidth
  }
})(TableCell);

export const haklaiSectionStyles = {
  section: {
    border: "1px solid #dce7ef",
    borderRadius: 12,
    boxShadow: "none",
    overflow: "hidden",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box"
  },
  sectionHeader: {
    background: "linear-gradient(90deg, #173a68 0%, #24558d 100%)",
    color: customColors.white,
    padding: 0,
    minHeight: 42,
    display: "flex",
    alignItems: "center"
  },
  sectionContent: {
    backgroundColor: "#f5faff",
    padding: "16px 20px",
    minWidth: 0,
    maxWidth: "100%",
    boxSizing: "border-box"
  }
};

export const buildHaklaiSectionChromeStyles = (theme, overrides = {}) => ({
  section: {
    ...haklaiSectionStyles.section,
    ...(overrides.section || {})
  },
  sectionHeader: {
    ...haklaiSectionStyles.sectionHeader,
    ...(overrides.sectionHeader || {})
  },
  sectionHeaderButton: {
    width: "100%",
    minHeight: 42,
    padding: "0 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    textAlign: "right",
    ...(overrides.sectionHeaderButton || {})
  },
  sectionHeaderTitle: {
    color: "inherit",
    fontSize: 16,
    fontWeight: 700,
    ...(overrides.sectionHeaderTitle || {})
  },
  sectionToggleIcon: {
    color: customColors.white,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    marginInlineStart: 8,
    fontSize: "1.45rem",
    transition: "transform 180ms ease",
    ...(overrides.sectionToggleIcon || {})
  },
  sectionToggleIconExpanded: {
    transform: "rotate(180deg)",
    ...(overrides.sectionToggleIconExpanded || {})
  },
  sectionContent: {
    ...haklaiSectionStyles.sectionContent,
    ...(overrides.sectionContent || {})
  }
});

export const haklaiLockableSectionStyles = {
  lockNotice: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 12,
    width: "min(460px, 100%)",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    margin: "0 auto 12px",
    padding: "10px 12px",
    border: `1px solid ${customColors.orange}`,
    backgroundColor: "rgba(255, 247, 230, 0.96)",
    borderRadius: 8
  },
  lockNoticeText: {
    color: "#8a5a00",
    fontWeight: 500,
    lineHeight: 1.35,
    minWidth: 0,
    overflowWrap: "break-word",
    textAlign: "center",
    flex: "1 1 auto"
  },
  lockedSectionContent: {
    opacity: 0.32,
    filter: "grayscale(0.18)",
    pointerEvents: "none",
    userSelect: "none"
  }
};

export const haklaiAddPillStyles = {
  addPillButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 28,
    height: 28,
    padding: 2,
    borderRadius: 6,
    border: "1.5px solid transparent",
    backgroundColor: "transparent",
    boxShadow: "none",
    transition: "background-color 180ms ease, border-color 180ms ease, box-shadow 180ms ease",
    "&:hover": {
      backgroundColor: "rgba(216, 139, 4, 0.08)"
    }
  },
  addPillButtonDisabled: {
    "&.Mui-disabled": {
      opacity: 1,
      backgroundColor: "rgba(220, 227, 230, 0.12)",
      borderColor: customColors.greyAndBlue,
      boxShadow: "none"
    }
  },
  addPillButtonReady: {
    backgroundColor: customColors.orange,
    borderColor: customColors.orange,
    boxShadow: "0px 3px 1px -2px rgb(0 0 0 / 20%), 0px 2px 2px 0px rgb(0 0 0 / 14%), 0px 1px 5px 0px rgb(0 0 0 / 12%)",
    "&:hover": {
      backgroundColor: "rgb(178, 123, 6)",
      borderColor: "rgb(178, 123, 6)",
      boxShadow: "0px 2px 4px -1px rgb(0 0 0 / 20%), 0px 4px 5px 0px rgb(0 0 0 / 14%), 0px 1px 10px 0px rgb(0 0 0 / 12%)"
    }
  },
  addIconReady: {
    color: "#1f1f1f",
    transition: "color 260ms cubic-bezier(0.22, 1, 0.36, 1)"
  },
  addIconDisabled: {
    color: "#1f1f1f",
    opacity: 1
  }
};

// Wrapper kept at `overflow: visible` on every breakpoint so sticky
// TableHead anchors to the page (its scroll-container ancestor is the
// viewport, not this box). We explicitly avoid `overflow-x: auto` here
// because that would (a) introduce a horizontal scrollbar scoped to the
// table and (b) turn the wrapper into an independent formatting context,
// which would stop the table's intrinsic width from propagating up to
// `.inner-con` — and the "card grows with its widest child" behaviour in
// `layout.css` depends on that propagation.
export const haklaiTableWrapperBaseStyles = {
  border: haklaiColumnBorder,
  borderRadius: 4,
  overflow: "visible",
  width: "100%",
  maxWidth: "100%",
  "--haklai-nav-height": "64px",
  "--haklai-header-height": "40px"
};

// Apply these to the header cells, never to `thead`. The calc table frame
// uses `overflow: clip`, and Chrome uses that frame as the sticky
// containing block. A nav offset (`top: 64px`) then opens an empty band
// above the header row. `top: 0` keeps the header flush with the table.
export const haklaiStickyHeaderStyles = {
  position: "sticky",
  top: 0,
  zIndex: 1,
  backgroundColor: "#d9e7f2"
};

// Room kept clear of the floating toolbar. A wide section grows the table
// into the leftover space; a narrow section keeps the natural column sum
// and scrolls instead.
export const HAKLAI_TABLE_EDGE_GUTTER = 48;

export const getHaklaiWideScreenTableWidth = (naturalWidth, availableWidth) => {
  if (!availableWidth || availableWidth <= 0) return naturalWidth;
  return Math.max(naturalWidth, availableWidth - HAKLAI_TABLE_EDGE_GUTTER);
};

// Grow the text columns by the same ratio, so a wide screen does not pour
// the leftover into one or two columns. The section-label and plus columns
// stay fixed so the two tables keep lining up. Rounding leftovers go to the
// columns with the largest fractional share so the widths still sum exactly.
export const addHaklaiColumnExtra = (widthsByKey, extra, flexibleKeys) => {
  const next = { ...widthsByKey };
  const keys = flexibleKeys.filter((key) => Object.prototype.hasOwnProperty.call(next, key));
  if (!keys.length || extra <= 0) return next;

  const base = keys.reduce((sum, key) => sum + next[key], 0);
  if (base <= 0) return next;

  const shares = keys.map((key) => (next[key] / base) * extra);
  const floors = shares.map((share) => Math.floor(share));
  let remainder = extra - floors.reduce((sum, share) => sum + share, 0);
  const byFraction = shares
    .map((share, index) => ({ index, fraction: share - floors[index] }))
    .sort((left, right) => right.fraction - left.fraction);
  byFraction.forEach(({ index }) => {
    if (remainder <= 0) return;
    floors[index] += 1;
    remainder -= 1;
  });
  keys.forEach((key, index) => {
    next[key] += floors[index];
  });
  return next;
};

// ─── Shared field-shell primitives ──────────────────────────────────────────
// `FieldShell`, `renderFieldErrorIndicator`, and `buildFieldErrorId` are the
// building blocks for the haklai tables' per-cell error surface: a fixed
// three-slot row (error "!" · input · currency adornment) whose height never
// changes as errors come and go, plus a focusable red "!" indicator that owns
// the accessible error message.
//
// CLASS-KEYS CONTRACT. Callers pass a JSS `classes` object that MUST define
// these keys (copy from `propertyParts/haklaiPropertyPartsStyles.js` or spread
// `buildHaklaiFieldShellStyles()` from this module):
//   - fieldShell
//   - fieldShellStatusSlot
//   - fieldShellInputSlot
//   - fieldShellCurrencySlot
//   - fieldShellCurrencyText
//   - fieldShellCurrencyHidden
//   - fieldErrorIcon
//   - fieldErrorIconHidden
//   - visuallyHidden

export const joinClassNames = (...names) => names.filter(Boolean).join(" ");

export const buildFieldErrorId = (rowKey, fieldName) => (
  rowKey && fieldName ? `haklai-${rowKey}-${fieldName}-err` : undefined
);

// The indicator is a compact red "!" that owns the accessible error message:
// - Focusable when there IS an error (keyboard users can reveal the tooltip).
// - Mirrors the message into a visually-hidden span so assistive tech reads it
//   even without hovering, and the input's `aria-describedby` points at it.
// - Always rendered (hidden via visibility) so the row height never changes
//   as errors come and go.
export const renderFieldErrorIndicator = (classes, errorMsg, errorId) => {
  const hasError = Boolean(errorMsg);
  return (
    <Box className={classes.fieldShellStatusSlot}>
      <Tooltip
        title={errorMsg || ""}
        placement="top"
        disableHoverListener={!hasError}
        disableFocusListener={!hasError}
        disableTouchListener={!hasError}
      >
        <span
          role={hasError ? "img" : undefined}
          aria-label={hasError ? errorMsg : undefined}
          aria-hidden={hasError ? undefined : true}
          tabIndex={hasError ? 0 : -1}
          className={joinClassNames(
            classes.fieldErrorIcon,
            !hasError ? classes.fieldErrorIconHidden : ""
          )}
        >
          !
        </span>
      </Tooltip>
      {hasError && errorId ? (
        <span id={errorId} className={classes.visuallyHidden}>
          {errorMsg}
        </span>
      ) : null}
    </Box>
  );
};

const renderFieldCurrencySlot = (classes, hidden = false, currencyLabel = "₪") => (
  <Box className={classes.fieldShellCurrencySlot}>
    <Typography
      component="span"
      className={joinClassNames(
        classes.fieldShellCurrencyText,
        hidden ? classes.fieldShellCurrencyHidden : ""
      )}
    >
      {currencyLabel || "₪"}
    </Typography>
  </Box>
);

export const FieldShell = ({
  classes,
  shellClassName = "",
  errorMsg = "",
  errorId,
  showCurrency = false,
  currencyHidden = false,
  currencyLabel = "₪",
  children
}) => (
  <Box className={joinClassNames(classes.fieldShell, shellClassName)}>
    {renderFieldErrorIndicator(classes, errorMsg, errorId)}
    <Box className={classes.fieldShellInputSlot}>
      {children}
    </Box>
    {showCurrency ? renderFieldCurrencySlot(classes, currencyHidden, currencyLabel) : null}
  </Box>
);

// ─── Shared design tokens for validation / shell / flash ────────────────────
// These keep the expenses + property-parts tables in lockstep visually. A
// future theme pass can swap the palette in one place. Kept co-located with
// the factories that consume them so drift is self-contained.
export const HAKLAI_FIELD_SLOT_GAP = 4;
export const HAKLAI_FIELD_ERROR_SLOT_WIDTH = 14;
export const HAKLAI_FIELD_CURRENCY_SLOT_WIDTH = 14;

export const HAKLAI_ERROR_PALETTE = {
  errorTextStrong: "#d32f2f",
  errorFlashPeak: "rgba(211, 47, 47, 0.18)",
  errorFlashBase: "rgba(211, 47, 47, 0.0)",
  focusRingError: "rgba(211, 47, 47, 0.35)"
};

// Factory returning the class keys `FieldShell` + `renderFieldErrorIndicator`
// depend on. Spread into any `makeStyles` sheet that needs to host a shell —
// this is the single source of truth for row-height stability (the "!" slot
// reserves space even when hidden).
export const buildHaklaiFieldShellStyles = () => ({
  fieldShell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: HAKLAI_FIELD_SLOT_GAP,
    maxWidth: "100%",
    margin: "0 auto"
  },
  fieldShellInputSlot: {
    flex: "0 0 auto",
    minWidth: 0
  },
  fieldShellStatusSlot: {
    width: HAKLAI_FIELD_ERROR_SLOT_WIDTH,
    minWidth: HAKLAI_FIELD_ERROR_SLOT_WIDTH,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  },
  fieldShellCurrencySlot: {
    width: HAKLAI_FIELD_CURRENCY_SLOT_WIDTH,
    minWidth: HAKLAI_FIELD_CURRENCY_SLOT_WIDTH,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  },
  fieldShellCurrencyText: {
    fontSize: "0.72rem",
    lineHeight: 1,
    color: customColors.darkGray
  },
  fieldShellCurrencyHidden: {
    visibility: "hidden"
  },
  fieldErrorIcon: {
    width: 14,
    height: 14,
    borderRadius: "50%",
    backgroundColor: HAKLAI_ERROR_PALETTE.errorTextStrong,
    color: "#fff",
    fontSize: "0.6rem",
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
    cursor: "help",
    userSelect: "none",
    outline: "none",
    "&:focus-visible": {
      boxShadow: `0 0 0 2px ${HAKLAI_ERROR_PALETTE.focusRingError}`
    }
  },
  fieldErrorIconHidden: {
    visibility: "hidden",
    pointerEvents: "none"
  },
  // Visually hidden but still accessible (WCAG technique). Paired with the
  // `aria-describedby` on each input so assistive tech reads the full error
  // sentence without the indicator having to expand.
  visuallyHidden: {
    position: "absolute",
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap",
    border: 0
  }
});

// Row-flash highlight used when save/analyze reveals a validation issue. The
// property-parts table uses cell `::after` pseudos for its dark-blue column
// frame, so the flash must not use a cell pseudo-element. Instead, pulse the
// cell surface and the MUI input surfaces separately; this keeps the blue
// frame stable while still tinting white/grey controls.
export const buildHaklaiRowFlashStyles = () => ({
  "@keyframes haklaiRowFlashSurface": {
    "0%": { backgroundColor: HAKLAI_ERROR_PALETTE.errorFlashBase },
    "25%": { backgroundColor: HAKLAI_ERROR_PALETTE.errorFlashPeak },
    "75%": { backgroundColor: HAKLAI_ERROR_PALETTE.errorFlashPeak },
    "100%": { backgroundColor: HAKLAI_ERROR_PALETTE.errorFlashBase }
  },
  "@keyframes haklaiRowFlashInputSurface": {
    "0%": {
      backgroundColor: "transparent",
      boxShadow: "inset 0 0 0 9999px rgba(211, 47, 47, 0)"
    },
    "25%": {
      backgroundColor: HAKLAI_ERROR_PALETTE.errorFlashPeak,
      boxShadow: "inset 0 0 0 9999px rgba(211, 47, 47, 0.08)"
    },
    "75%": {
      backgroundColor: HAKLAI_ERROR_PALETTE.errorFlashPeak,
      boxShadow: "inset 0 0 0 9999px rgba(211, 47, 47, 0.08)"
    },
    "100%": {
      backgroundColor: "transparent",
      boxShadow: "inset 0 0 0 9999px rgba(211, 47, 47, 0)"
    }
  },
  rowFlash: {
    "& > .MuiTableCell-root:not([rowspan])": {
      animation: "$haklaiRowFlashSurface 700ms ease-in-out"
    },
    "& > .MuiTableCell-root:not([rowspan]) .MuiInputBase-root": {
      animation: "$haklaiRowFlashInputSurface 700ms ease-in-out"
    },
    "& > .MuiTableCell-root:not([rowspan]) .MuiSelect-select, & > .MuiTableCell-root:not([rowspan]) .MuiInputBase-input": {
      backgroundColor: "transparent"
    }
  }
});
